import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Flex,
  Text,
  Table,
  Link,
  Tooltip,
  Button,
} from '@radix-ui/themes';
import { formatNumber } from '../../../lib/helpers';
import styles from './BroadcastMessage.module.css';

// Date when email open tracking feature went live
const EMAIL_OPENS_GO_LIVE_DATE = import.meta.env.VITE_EMAIL_OPENS_GO_LIVE_DATE
  ? new Date(import.meta.env.VITE_EMAIL_OPENS_GO_LIVE_DATE)
  : null;

const BROADCAST_TYPE_COLORS = {
  email: 'blue',
  sms: 'violet',
  both: 'green',
};

const BroadcastTypeBadge = ({ type }) => {
  const { t } = useTranslation('reports');

  const labels = {
    email: t('broadcasts.email'),
    sms: t('broadcasts.sms'),
    both: t('broadcasts.both'),
  };

  return (
    <Badge
      color={BROADCAST_TYPE_COLORS[type] || 'blue'}
      variant="soft"
      size="1"
      className={styles.typeBadge}
    >
      {labels[type] || type}
    </Badge>
  );
};

const CHANNEL_COLORS = {
  email: 'blue',
  sms: 'violet',
  both: 'green',
};

const ChannelBadge = ({ channel }) => {
  const { t } = useTranslation('reports');

  const labels = {
    email: t('broadcasts.email'),
    sms: t('broadcasts.sms'),
    both: t('broadcasts.both'),
  };

  return (
    <Badge color={CHANNEL_COLORS[channel] || 'gray'} variant="soft" size="1">
      {labels[channel] || channel}
    </Badge>
  );
};

const truncateUrl = (text, maxLength = 50) => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

const getLinkDisplayText = link => {
  return link.displayName || link.url;
};

const BroadcastMessage = ({ broadcast, linkTrackingDisabled = false }) => {
  const { t } = useTranslation('reports');
  const [showLinkBreakdown, setShowLinkBreakdown] = useState(false);

  const isEmailOpensDataAvailable = () => {
    if (!EMAIL_OPENS_GO_LIVE_DATE || !broadcast.sentAtRaw) return false;
    const sentDate = new Date(broadcast.sentAtRaw);
    return sentDate >= EMAIL_OPENS_GO_LIVE_DATE;
  };

  const isLinkClickDataAvailable =
    isEmailOpensDataAvailable() && !linkTrackingDisabled;

  const hasLinkBreakdown =
    broadcast.linkBreakdown && broadcast.linkBreakdown.length > 0;

  const toggleLinkBreakdown = () => {
    if (hasLinkBreakdown) {
      setShowLinkBreakdown(!showLinkBreakdown);
    }
  };

  const subject =
    broadcast.subject || broadcast.contentPreview || t('broadcasts.noSubject');

  return (
    <Box className={styles.wrapper}>
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center" gap="3">
          <Text size="3" weight="bold" data-testid="broadcast-subject">
            {subject}
          </Text>
          <Text size="2" color="gray">
            {broadcast.sentAt}
          </Text>
        </Flex>

        <Flex justify="between" align="center" gap="4" wrap="wrap">
          <Flex align="center" gap="4" wrap="wrap">
            <BroadcastTypeBadge type={broadcast.broadcastType} />
            <Box className={styles.divider} />
            <Flex align="center" gap="1">
              <Text size="2" color="gray">
                {t('broadcasts.recipients')}:
              </Text>
              <Text size="2" weight="medium">
                {formatNumber(broadcast.totalAudience)}
              </Text>
            </Flex>
            <Box className={styles.divider} />
            <Text size="2" weight="medium">
              {broadcast.audienceNames || t('broadcasts.allUsers')}
            </Text>
          </Flex>
          <Link
            href={`/client_admin/broadcast_messages#${broadcast.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              variant="soft"
              size="1"
              color="gray"
              className={styles.viewBroadcastButton}
            >
              {t('broadcasts.viewBroadcast')}
              <i className="fa-solid fa-arrow-up-right-from-square" />
            </Button>
          </Link>
        </Flex>
      </Flex>

      <Box className={styles.statsSection}>
        <Flex gap="3">
          {broadcast.broadcastType !== 'sms' && (
            <>
              <Tooltip
                content={
                  isEmailOpensDataAvailable()
                    ? t('broadcasts.emailOpensTooltip')
                    : t('broadcasts.emailOpensNotAvailable')
                }
              >
                <Box
                  className={
                    isEmailOpensDataAvailable()
                      ? styles.statCard
                      : styles.statCardDisabled
                  }
                >
                  <Text
                    size="7"
                    weight="bold"
                    className={styles.statValue}
                    data-testid="broadcast-total-opens"
                  >
                    {isEmailOpensDataAvailable()
                      ? formatNumber(broadcast.totalEmailOpens)
                      : '-'}
                  </Text>
                  <Text size="1" weight="medium" color="gray">
                    {t('broadcasts.emailOpens')}
                  </Text>
                </Box>
              </Tooltip>

              <Tooltip
                content={
                  isEmailOpensDataAvailable()
                    ? t('broadcasts.uniqueOpensTooltip')
                    : t('broadcasts.emailOpensNotAvailable')
                }
              >
                <Box
                  className={
                    isEmailOpensDataAvailable()
                      ? styles.statCard
                      : styles.statCardDisabled
                  }
                >
                  <Text
                    size="7"
                    weight="bold"
                    className={styles.statValue}
                    data-testid="broadcast-unique-opens"
                  >
                    {isEmailOpensDataAvailable()
                      ? formatNumber(broadcast.uniqueEmailOpens)
                      : '-'}
                  </Text>
                  <Text size="1" weight="medium" color="gray">
                    {t('broadcasts.uniqueOpens')}
                  </Text>
                </Box>
              </Tooltip>
            </>
          )}

          <Tooltip
            content={
              linkTrackingDisabled
                ? t('broadcasts.linkTrackingDisabled')
                : isEmailOpensDataAvailable()
                ? t('broadcasts.linkClicksTooltip')
                : t('broadcasts.emailOpensNotAvailable')
            }
          >
            <Box
              className={
                isLinkClickDataAvailable
                  ? styles.statCard
                  : styles.statCardDisabled
              }
            >
              <Text
                size="7"
                weight="bold"
                className={styles.statValue}
                data-testid="broadcast-total-clicks"
              >
                {isLinkClickDataAvailable
                  ? formatNumber(broadcast.totalLinkClicks)
                  : '-'}
              </Text>
              <Text size="1" weight="medium" color="gray">
                {t('broadcasts.linkClicks')}
              </Text>
            </Box>
          </Tooltip>

          <Tooltip
            content={
              linkTrackingDisabled
                ? t('broadcasts.linkTrackingDisabled')
                : isEmailOpensDataAvailable()
                ? t('broadcasts.uniqueClicksTooltip')
                : t('broadcasts.emailOpensNotAvailable')
            }
          >
            <Box
              className={
                isLinkClickDataAvailable
                  ? styles.statCard
                  : styles.statCardDisabled
              }
            >
              <Text
                size="7"
                weight="bold"
                className={styles.statValue}
                data-testid="broadcast-unique-clicks"
              >
                {isLinkClickDataAvailable
                  ? formatNumber(broadcast.uniqueLinkClicks)
                  : '-'}
              </Text>
              <Text size="1" weight="medium" color="gray">
                {t('broadcasts.uniqueClicks')}
              </Text>
            </Box>
          </Tooltip>
        </Flex>

        {hasLinkBreakdown && isLinkClickDataAvailable && (
          <Box mt="3">
            <Button
              variant="ghost"
              size="2"
              onClick={toggleLinkBreakdown}
              className={styles.expandButton}
              data-testid="clicks-by-link-button"
            >
              <i
                className={`fa-solid fa-chevron-${
                  showLinkBreakdown ? 'up' : 'down'
                } ${styles.chevron}`}
              />
              <Text size="2" weight="bold">
                {t('broadcasts.linkBreakdownTitle')}
              </Text>
            </Button>
          </Box>
        )}
      </Box>

      {showLinkBreakdown && hasLinkBreakdown && isLinkClickDataAvailable && (
        <Box className={styles.linkBreakdownSection}>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>
                  {t('broadcasts.linkUrl')}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell align="center" width="100px">
                  {t('broadcasts.channel')}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell align="center" width="120px">
                  {t('broadcasts.totalClicks')}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell align="center" width="120px">
                  {t('broadcasts.uniqueClicksColumn')}
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {broadcast.linkBreakdown.map(link => {
                const displayText = getLinkDisplayText(link);
                const isInternal = link.isInternal;

                return (
                  <Table.Row key={link.url}>
                    <Table.Cell>
                      <Tooltip content={isInternal ? displayText : link.url}>
                        {isInternal ? (
                          <Text size="2">{truncateUrl(displayText)}</Text>
                        ) : (
                          <Link
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {truncateUrl(displayText)}
                          </Link>
                        )}
                      </Tooltip>
                    </Table.Cell>
                    <Table.Cell align="center">
                      <ChannelBadge channel={link.channel} />
                    </Table.Cell>
                    <Table.Cell align="center">
                      {formatNumber(link.totalClicks)}
                    </Table.Cell>
                    <Table.Cell align="center">
                      {formatNumber(link.uniqueClicks)}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </Box>
  );
};

BroadcastMessage.propTypes = {
  broadcast: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    sentAt: PropTypes.string,
    sentAtRaw: PropTypes.string,
    broadcastType: PropTypes.string,
    subject: PropTypes.string,
    contentPreview: PropTypes.string,
    totalAudience: PropTypes.number,
    audienceNames: PropTypes.string,
    totalEmailOpens: PropTypes.number,
    uniqueEmailOpens: PropTypes.number,
    totalLinkClicks: PropTypes.number,
    uniqueLinkClicks: PropTypes.number,
    linkBreakdown: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        displayName: PropTypes.string,
        totalClicks: PropTypes.number,
        uniqueClicks: PropTypes.number,
        isInternal: PropTypes.bool,
        channel: PropTypes.string,
      }),
    ),
  }).isRequired,
  linkTrackingDisabled: PropTypes.bool,
};

export default BroadcastMessage;
